/**
 * Syncs bulk new-report state to backgroundJobNoticesStore and coordinates terminal dismiss confirm.
 */
import { useCallback, useEffect, useState } from "react";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import backgroundJobNoticesStore, {
    BACKGROUND_JOB_NOTICE_STATE,
} from "../../stores/background-job-notice-store";
import bulkRmaActionStore from "../../stores/bulk-rma-action-store";
import BulkActionNoticeDescription from "./bulk-action-notice-description";
import {
    BULK_ACTION_NOTICE_TITLE_KEY,
    getBulkActionNoticeState,
} from "./bulk-action-notice-helpers";

const DASHBOARD_RETURNS_SCOPE = "dashboard-returns-page";

const TERMINAL_STATES = new Set<BACKGROUND_JOB_NOTICE_STATE>([
    BACKGROUND_JOB_NOTICE_STATE.COMPLETE,
    BACKGROUND_JOB_NOTICE_STATE.PARTIAL,
    BACKGROUND_JOB_NOTICE_STATE.STOPPED,
]);

export interface IBulkRmaActionNoticeDismissModalState {
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: () => Promise<void>;
}

export function useBulkRmaActionNotice(): IBulkRmaActionNoticeDismissModalState | null {
    const { getTranslation } = useDashboardTranslation();
    const [dismissOpen, setDismissOpen] = useState(false);

    const onViewResults = useCallback(() => {
        rmsMixpanelStore.trackEvent("d&_bulk_details_open");
        void bulkRmaActionStore.openPanel();
    }, []);

    const onTerminalClose = useCallback(() => {
        setDismissOpen(true);
    }, []);

    const featureEnabled = bulkRmaActionStore.isFeatureEnabled;
    const isPanelOpen = bulkRmaActionStore.isPanelOpen;
    const noticeState = bulkRmaActionStore.newReport
        ? getBulkActionNoticeState(bulkRmaActionStore.newReport)
        : null;

    useEffect(() => {
        const report = bulkRmaActionStore.newReport;
        if (!featureEnabled || !report || !noticeState || isPanelOpen || dismissOpen) {
            return;
        }

        const isTerminal = TERMINAL_STATES.has(noticeState);
        backgroundJobNoticesStore.show({
            state: noticeState,
            title: getTranslation(
                BULK_ACTION_NOTICE_TITLE_KEY[noticeState],
                DASHBOARD_RETURNS_SCOPE,
            ),
            description: <BulkActionNoticeDescription />,
            actionButton: isTerminal
                ? {
                      title: getTranslation(
                          "BULK_ACTION_NOTICE_VIEW_RESULTS",
                          DASHBOARD_RETURNS_SCOPE,
                      ),
                      onClick: onViewResults,
                  }
                : undefined,
            onClose: isTerminal ? onTerminalClose : undefined,
        });

        return () => {
            backgroundJobNoticesStore.clear();
        };
    }, [
        featureEnabled,
        noticeState,
        isPanelOpen,
        dismissOpen,
        getTranslation,
        onViewResults,
        onTerminalClose,
    ]);

    const onDismissCancel = useCallback(() => {
        setDismissOpen(false);
    }, []);

    const onDismissConfirm = useCallback(async () => {
        rmsMixpanelStore.trackEvent("d&_bulk_dismiss");
        const ok = await bulkRmaActionStore.dismissNewReport();
        if (ok) {
            setDismissOpen(false);
        }
    }, []);

    if (!dismissOpen) {
        return null;
    }

    return {
        isLoading: bulkRmaActionStore.isDismissing,
        onCancel: onDismissCancel,
        onConfirm: onDismissConfirm,
    };
}
